import React, { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { FormikHelpers } from 'formik'
import cloneDeep from 'lodash.clonedeep'

import { useComments } from '@hooks/comment/useComments'
import { usePagination } from '@hooks/usePagination'
import { useAuthentication } from '@hooks/useAuthentication'
import { useCreateComment } from '@hooks/comment/useCreateComment'
import { useUpdateComment } from '@hooks/comment/useUpdateComment'
import { useRemoveComment } from '@hooks/comment/useRemoveComment'
import { CommentsResponse } from '@models/comment/CommentsResponse'
import { useAlert } from '@hooks/useAlert'
import { queryClient } from '@client/init'
import { LikeSuccessStatusEnum } from '@models/book/LikeSuccessStatusEnum'
import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { useBlockComment } from '@hooks/comment/useBlockComment'
import { useTheme } from '@hooks/contexts/ThemeContext/ThemeContext'
import { BlockedTypeEnum } from '@interfaces/BlockedTypeEnum'
import { NotificationActionEnum } from '@interfaces/NotificationActionEnum'
import { scrollToElement } from '@lib/utils'
import { useCommentAction } from '@hooks/comment/useCommentAction'
import { useNotificationAction } from '@hooks/notification/useNotificationAction'
import { useChallengeAction } from '@hooks/challenge/useChallengeAction'
import { CommentAcceptProps, CommentProps } from './interface'
import { ReplyUser } from './components/TiptapComment/interface'

const emptyValue = '<p></p>'

export function withComment(Component: React.FC<CommentProps>) {
  function WithComment({
    bookId,
    chapterId,
    lastCommentDate,
    refecthKey,
    commentable,
    guestCommentable,
    commentCount,
    authorUserId,
    isDrawer = false,
    isUserHasBlockedComment,
    className,
  }: CommentAcceptProps) {
    const [replyUser, setReplyUser] = useState<ReplyUser>()
    const [editCommentId, setEditCommentId] = useState<number>()
    const [isReadyToFetchComment, setIsReadyToFetchComment] = useState(false)
    const alert = useAlert()
    const loginModal = useModal({ modal: 'login' })
    const { user, isAuthenticated } = useAuthentication()
    const { query, isReady, replace } = useRouter()
    const parentCommentId = query.parentCommentId
      ? Number(query.parentCommentId)
      : undefined
    const commentId = query.commentId ? Number(query.commentId) : undefined
    const action = query.action ? query.action.toString() : undefined
    const confirmDeleteModal = useModal({ modal: 'confirm' }, true)
    const confirmChallengeCommentModal = useModal(
      {
        modal: 'confirmChallengeComment',
      },
      true
    )
    const { page, perpage, pageChange, perpageChange } = usePagination()
    const { theme } = useTheme()
    const commentClient = useCommentAction()
    const notificationClient = useNotificationAction()
    const challengeClient = useChallengeAction()

    useEffect(() => {
      setIsReadyToFetchComment(false)
      async function fetchCommentPage(params: {
        commentId: number
        bookId?: number
        bookChapterId?: number
      }) {
        const commentPage = await notificationClient.getNotificationCommentPage(
          params
        )
        pageChange(commentPage)
        setIsReadyToFetchComment(true)
      }

      if (!isReady) return
      if (commentId) {
        fetchCommentPage({
          commentId:
            action === NotificationActionEnum.NEW_REPLY && parentCommentId
              ? parentCommentId
              : commentId,
          bookId: chapterId ? undefined : bookId,
          bookChapterId: chapterId,
        })
      } else {
        setIsReadyToFetchComment(true)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isReady, bookId, parentCommentId, commentId, action, chapterId])

    const {
      data: commentsData,
      isLoading: isCommentsLoading,
      refetch: refetchComments,
    } = useComments(
      {
        limitPerPage: perpage,
        page,
        where: {
          bookId,
          bookChapterId: chapterId,
        },
      },
      {
        enabled: isReadyToFetchComment,
        onSuccess: () => {
          scrollToComment()
        },
      }
    )

    function scrollToComment() {
      if (commentId) {
        setTimeout(() => {
          const elem = document.getElementById(`comment-${commentId}`)
          if (elem) {
            elem.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            })
            replace(window.location.pathname, undefined, {
              shallow: true,
            })
          }
        }, 1000)
      }
    }

    const { mutate: createComment } = useCreateComment(
      () => {
        refetchComments()
        queryClient.invalidateQueries(refecthKey)
        alert.success('เพิ่มความคิดเห็นสำเร็จ')
      },
      () => {
        alert.error('เกิดข้อผิดพลาดในการเชื่อมต่อ กรุณาลองอีกครั้ง')
      }
    )

    const { mutate: updateComment } = useUpdateComment(
      (_, variables) => {
        refetchComments()
        variables.visible !== undefined
          ? variables.visible
            ? alert.success('ยกเลิกซ่อนความคิดเห็นสำเร็จ')
            : alert.success('ซ่อนความคิดเห็นสำเร็จ')
          : alert.success('แก้ไขความคิดเห็นสำเร็จ')
      },
      () => {
        alert.error('เกิดข้อผิดพลาดในการเชื่อมต่อ กรุณาลองอีกครั้ง')
      }
    )

    const { mutate: deleteComment } = useRemoveComment(
      () => {
        refetchComments()
        alert.success('ลบความคิดเห็นสำเร็จ')
      },
      () => {
        alert.error('เกิดข้อผิดพลาดในการเชื่อมต่อ กรุณาลองอีกครั้ง')
      }
    )

    const { mutate: blockComment } = useBlockComment({
      onSuccess: () => {
        refetchComments()
        alert.success('บล็อกการแสดงความคิดเห็นสำเร็จ')
      },
      onError: () => {
        alert.error(
          'คุณได้บล็อกการแสดงความคิดเห็นของผู้ใช้งานนี้แล้ว ตรวจสอบได้ที่ "ตั้งค่าโปรไฟล์"'
        )
      },
    })

    function replyComment(reply: {
      id?: number
      name: string
      rootId: number
    }) {
      setReplyUser(reply)
    }

    function onCancelEditComment() {
      setEditCommentId(undefined)
    }

    function onEditCommentSubmit(
      values: { content: string; wordCount: number },
      formikBag: FormikHelpers<{
        content: string
        wordCount: number
      }>
    ) {
      if (values.content !== emptyValue) {
        updateComment({ id: editCommentId!, content: values.content })
        formikBag.resetForm()
        onCancelEditComment()
      }
    }

    function manageComment(id: number, option: string, blockUserId: number) {
      if (option === 'delete') {
        confirmDeleteModal.show({
          content: (
            <div className='w-[400px] mb-[20px] mobile:w-[248px]'>
              <p className='font-mitr text-center text-[24px] text-secondary dark:text-white-2 font-medium '>
                ยืนยันการลบความคิดเห็นหรือไม่?
              </p>
            </div>
          ),
          onConfirm: () => {
            deleteComment(Number(id))
            confirmDeleteModal.hide()
          },
          onClose: () => {
            confirmDeleteModal.hide()
          },
        })
      }
      if (option === 'hidden') {
        updateComment({ id, visible: false })
      }
      if (option === 'edit') {
        setEditCommentId(id)
      }
      if (option === 'block') {
        blockComment({ id: blockUserId, blockType: BlockedTypeEnum.COMMENT })
      }
    }

    async function likeComment(id: number, rootId: number) {
      if (isAuthenticated) {
        const type = await commentClient.toggleCommentLike(id)

        queryClient.setQueryData<CommentsResponse | undefined>(
          [
            'comments',
            {
              limitPerPage: perpage,
              page,
              where: {
                bookId,
                bookChapterId: chapterId,
              },
            },
          ],
          (oldData?: CommentsResponse) => {
            if (oldData) {
              const temp = cloneDeep(oldData)
              const index = temp.data.findIndex(row => row.id === id)
              if (index !== -1) {
                temp.data[index].isLiked = type === LikeSuccessStatusEnum.CREATE
                temp.data[index].likeCount =
                  type === LikeSuccessStatusEnum.CREATE
                    ? temp.data[index].likeCount + 1
                    : Math.max(0, temp.data[index].likeCount - 1)
              } else {
                const rootIndex = temp.data.findIndex(row => row.id === rootId)
                if (rootIndex !== -1) {
                  const childIndex = temp.data[
                    rootIndex
                  ].childrenComment.findIndex(row => row.id === id)
                  if (childIndex !== -1) {
                    temp.data[rootIndex].childrenComment[childIndex].isLiked =
                      type === LikeSuccessStatusEnum.CREATE
                    temp.data[rootIndex].childrenComment[childIndex].likeCount =
                      type === LikeSuccessStatusEnum.CREATE
                        ? temp.data[rootIndex].childrenComment[childIndex]
                            .likeCount + 1
                        : Math.max(
                            0,
                            temp.data[rootIndex].childrenComment[childIndex]
                              .likeCount - 1
                          )
                  }
                }
              }
              return temp
            }
            return oldData
          }
        )
      } else {
        loginModal.show()
      }
    }

    function unHiddenComment(id: number) {
      updateComment({ id, visible: true })
    }

    async function onMainCommentSubmit(
      values: { content: string; wordCount: number },
      formikBag: FormikHelpers<{
        content: string
        wordCount: number
      }>
    ) {
      if (values.content !== emptyValue) {
        if (isAuthenticated) {
          const checkCommentResp = await challengeClient.checkCommentChallenge(
            values.wordCount
          )
          if (
            checkCommentResp?.isHasChallenge &&
            checkCommentResp?.remainingCount &&
            checkCommentResp.remainingCount > 0
          ) {
            confirmChallengeCommentModal.show({
              data: checkCommentResp,
              onClose: () => confirmChallengeCommentModal.hide(),
              onSubmit: () => {
                createComment({
                  bookId,
                  bookChapterId: chapterId,
                  content: values.content,
                  wordCount: values.wordCount,
                })
                confirmChallengeCommentModal.hide()
                formikBag.resetForm()
                setReplyUser(undefined)
              },
            })
          } else {
            createComment({
              bookId,
              bookChapterId: chapterId,
              content: values.content,
              wordCount: values.wordCount,
            })
            formikBag.resetForm()
            setReplyUser(undefined)
          }
        } else {
          createComment({
            bookId,
            bookChapterId: chapterId,
            content: values.content,
            wordCount: values.wordCount,
          })
          formikBag.resetForm()
          setReplyUser(undefined)
        }
      }
    }

    async function onReplyCommentSubmit(
      values: { content: string; wordCount: number },
      formikBag: FormikHelpers<{
        content: string
        wordCount: number
      }>
    ) {
      if (values.content !== emptyValue) {
        createComment({
          bookId,
          content: values.content,
          repliedId: replyUser?.id,
          parentCommentId: replyUser?.rootId,
          wordCount: 0,
        })
        formikBag.resetForm()
        setReplyUser(undefined)
      }
    }

    function handlePaginationChange(newPage: number) {
      pageChange(newPage)

      if (isDrawer) {
        const el = document.querySelector('#comment')

        if (el) {
          el.scrollIntoView()
        }
      } else {
        scrollToElement('#comment')
      }
    }

    if (!isCommentsLoading && commentsData) {
      const newProps = {
        user,
        isAuthenticated,
        comments: commentsData?.data,
        replyUser,
        className,
        onMainCommentSubmit,
        replyComment,
        likeComment,
        manageComment,
        unHiddenComment,
        page,
        perpage,
        total: commentsData?.total || 0,
        handlePaginationChange,
        perpageChange,
        onReplyCommentSubmit,
        editCommentId,
        onCancelEditComment,
        onEditCommentSubmit,
        lastCommentDate,
        isCommentAble: isAuthenticated ? commentable : guestCommentable,
        commentCount,
        authorUserId,
        isDrawer,
        isUserHasBlockedComment,
        visibleSubComment: action === NotificationActionEnum.NEW_REPLY,
        theme,
      }
      return <Component {...newProps} />
    }
    return <></>
  }

  return WithComment
}
